import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { 
  Calendar, 
  Clock, 
  Users, 
  FileText, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  RotateCcw,
  Eye,
  Play
} from 'lucide-react';
import { useRolloverMutations } from '@/hooks/leave/useRolloverMutations';
import { useQuery } from '@tanstack/react-query';
import { rolloverAuditService } from '@/services/leave/rolloverAuditService';
import { format } from 'date-fns';

export function RolloverManagement() {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear() - 1);
  const [showPreview, setShowPreview] = useState(false);
  
  const { 
    processAnnualRollover,
    generatePreview,
    isGeneratingPreview,
    isProcessing,
    previewData
  } = useRolloverMutations();

  // Get audit logs
  const { data: auditLogs = [], isLoading: isLoadingAuditLogs } = useQuery({
    queryKey: ['rollover-audit-logs'],
    queryFn: () => rolloverAuditService.getAuditLogs({ limit: 10 }),
  });

  // Get scheduled expirations
  const { data: upcomingExpirations = [] } = useQuery({
    queryKey: ['upcoming-expirations'],
    queryFn: () => rolloverAuditService.getScheduledExpirations({ 
      upcoming_days: 30,
      status: 'scheduled'
    }),
  });

  const handleGeneratePreview = async () => {
    setShowPreview(true);
    await generatePreview.mutateAsync(selectedYear);
  };

  const handleExecuteRollover = async () => {
    await processAnnualRollover.mutateAsync(selectedYear);
    setShowPreview(false);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      'completed': { variant: 'default' as const, icon: CheckCircle, color: 'text-green-600' },
      'failed': { variant: 'destructive' as const, icon: XCircle, color: 'text-red-600' },
      'in_progress': { variant: 'secondary' as const, icon: Clock, color: 'text-blue-600' },
      'cancelled': { variant: 'outline' as const, icon: XCircle, color: 'text-gray-600' }
    };
    
    const config = variants[status as keyof typeof variants] || variants.in_progress;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Rollover Management</h2>
          <p className="text-muted-foreground">
            Manage annual leave rollovers and view audit trails
          </p>
        </div>
      </div>

      <Tabs defaultValue="rollover" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="rollover">Annual Rollover</TabsTrigger>
          <TabsTrigger value="audit">Audit Trail</TabsTrigger>
          <TabsTrigger value="expirations">Expirations</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="rollover" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5" />
                Annual Leave Rollover
              </CardTitle>
              <CardDescription>
                Process annual leave balance rollovers for the selected year
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="rollover-year">Rollover Year</Label>
                  <Input
                    id="rollover-year"
                    type="number"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    placeholder="e.g., 2023"
                  />
                  <p className="text-sm text-muted-foreground">
                    Leave balances from this year will be rolled over to {selectedYear + 1}
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={handleGeneratePreview}
                      disabled={isGeneratingPreview || isProcessing}
                      className="flex-1"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      {isGeneratingPreview ? 'Generating Preview...' : 'Preview Rollover'}
                    </Button>
                    
                    <Button
                      onClick={handleExecuteRollover}
                      disabled={!showPreview || isProcessing || isGeneratingPreview}
                      className="flex-1"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      {isProcessing ? 'Processing...' : 'Execute Rollover'}
                    </Button>
                  </div>
                  
                  {isProcessing && (
                    <div className="space-y-2">
                      <Progress value={45} className="w-full" />
                      <p className="text-sm text-muted-foreground">Processing rollover...</p>
                    </div>
                  )}
                </div>
              </div>

              {showPreview && previewData && (
                <div className="space-y-4">
                  <Separator />
                  <div>
                    <h4 className="font-semibold mb-3">Rollover Preview for {selectedYear}</h4>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <Card className="p-4">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-blue-600" />
                          <div>
                            <p className="text-2xl font-bold">{previewData.employee_count}</p>
                            <p className="text-sm text-muted-foreground">Employees</p>
                          </div>
                        </div>
                      </Card>
                      
                      <Card className="p-4">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-green-600" />
                          <div>
                            <p className="text-2xl font-bold">{previewData.policies_affected}</p>
                            <p className="text-sm text-muted-foreground">Policies</p>
                          </div>
                        </div>
                      </Card>
                      
                      <Card className="p-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-purple-600" />
                          <div>
                            <p className="text-2xl font-bold">{previewData.total_carryover}</p>
                            <p className="text-sm text-muted-foreground">Days Carried Over</p>
                          </div>
                        </div>
                      </Card>
                      
                      <Card className="p-4">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-orange-600" />
                          <div>
                            <p className="text-2xl font-bold">~{previewData.estimated_duration_minutes}</p>
                            <p className="text-sm text-muted-foreground">Minutes</p>
                          </div>
                        </div>
                      </Card>
                    </div>

                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Ready to Process</AlertTitle>
                      <AlertDescription>
                        This rollover will affect {previewData.employee_count} employees across {previewData.policies_affected} leave policies. 
                        The operation is estimated to take approximately {previewData.estimated_duration_minutes} minutes.
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Audit Trail
              </CardTitle>
              <CardDescription>
                View rollover operation history and audit logs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingAuditLogs ? (
                <div className="text-center py-8">Loading audit logs...</div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No rollover operations found
                </div>
              ) : (
                <div className="space-y-4">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div>
                            <h4 className="font-semibold">
                              {log.operation_type.replace('_', ' ').toUpperCase()} - {log.rollover_year} → {log.target_year}
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              Started {format(new Date(log.started_at), 'PPpp')}
                            </p>
                          </div>
                        </div>
                        {getStatusBadge(log.status)}
                      </div>
                      
                      {log.status === 'completed' && (
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="font-medium">Employees:</span> {log.employee_count}
                          </div>
                          <div>
                            <span className="font-medium">Policies:</span> {log.policies_processed}
                          </div>
                          <div>
                            <span className="font-medium">Duration:</span> {log.execution_duration_ms && Math.round(log.execution_duration_ms / 1000)}s
                          </div>
                        </div>
                      )}
                      
                      {log.status === 'failed' && log.error_details && (
                        <Alert variant="destructive">
                          <XCircle className="h-4 w-4" />
                          <AlertTitle>Operation Failed</AlertTitle>
                          <AlertDescription>
                            {(log.error_details as any)?.message || 'Unknown error occurred'}
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expirations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Balance Expirations
              </CardTitle>
              <CardDescription>
                Manage carry-over balance expiration schedules
              </CardDescription>
            </CardHeader>
            <CardContent>
              {upcomingExpirations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No scheduled expirations in the next 30 days
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingExpirations.map((expiration) => (
                    <div key={expiration.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{expiration.employee_name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {expiration.policy_name} - {expiration.carried_over_days} days expire on {format(new Date(expiration.expiration_date), 'PP')}
                          </p>
                        </div>
                        <Badge variant="outline">
                          {expiration.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Rollover Reports
              </CardTitle>
              <CardDescription>
                Generate and download rollover reports
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                Rollover reporting functionality coming soon...
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}